/**
 * Root layout — auth gate + providers.
 */

import React, { useEffect, useState, useRef } from 'react';
import { Stack, useRouter, useSegments } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { I18nextProvider } from 'react-i18next';
import { useFonts, Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold } from '@expo-google-fonts/inter';
import { PlusJakartaSans_600SemiBold, PlusJakartaSans_700Bold } from '@expo-google-fonts/plus-jakarta-sans';
import * as SplashScreen from 'expo-splash-screen';
import * as Linking from 'expo-linking';
import i18n, { loadPersistedLanguage } from '../src/i18n';
import { useAuthStore } from '../src/stores/authStore';
import { View, ActivityIndicator, StyleSheet, Platform, AppState, Image, Text, AccessibilityInfo, TouchableOpacity } from 'react-native';
import { setServerUrlOverride, normalizeServerUrl, setLanguages } from '../src/utils/constants';
import { ThemeProvider, useTheme } from '../src/utils/theme';
import { getServerUrl } from '../src/utils/storage';
import { configureNotifications, requestPermissions, getPushToken, addNotificationActionListener } from '../src/utils/notifications';
import { registerBackgroundSync } from '../src/utils/backgroundSync';
import { useRemindersStore } from '../src/stores/reminderStore';
import { useChatStore } from '../src/stores/chatStore';
import { registerDevice, fetchLanguages, resetClient } from '../src/api/client';
import { getDeviceId } from '../src/utils/storage';
import { ErrorBoundary } from '../src/components/common/ErrorBoundary';
import { needsInitialSetup } from '../src/utils/onboarding';
import { useNavigationStore } from '../src/stores/navigationStore';
import { initAnalytics, trackScreen, setUserProperties } from '../src/utils/analytics';
import { initSentry, setSentryUser, addBreadcrumb } from '../src/utils/sentry';
import { isBiometricEnabled, authenticateWithBiometric } from '../src/utils/biometric';
import InAppToast from '../src/components/common/InAppToast';
import NotificationRationale from '../src/components/onboarding/NotificationRationale';
import { initWidgetSync, cleanupWidgetSync } from '../src/native/widgetSyncSetup';
import { startOfflineQueueReplay } from '../src/stores/offlineQueueReplay';

// Configure notification handler before any component mounts
configureNotifications();

// Web safety net: some navigation libraries (and our redirect chain) can
// emit history.replaceState in a tight loop when the navigation state object
// reference changes but the URL does not. Chrome eventually throttles this
// with a "Throttling navigation to prevent the browser from hanging" warning
// and the page becomes unresponsive. Dedupe identical, back-to-back replace
// calls so a runaway caller can't lock up the tab.
if (typeof window !== 'undefined' && typeof window.history?.replaceState === 'function' && !(window as any).__fmReplaceStatePatched) {
  (window as any).__fmReplaceStatePatched = true;
  const _origReplace = window.history.replaceState.bind(window.history);
  let _lastUrl: string | null = null;
  let _lastAt = 0;
  window.history.replaceState = function (state: any, title: string, url?: string | null) {
    try {
      const u = url == null ? window.location.pathname + window.location.search + window.location.hash : String(url);
      const now = Date.now();
      // If we just replaced to the same URL within 50ms, skip.
      if (u === _lastUrl && now - _lastAt < 50) {
        return;
      }
      _lastUrl = u;
      _lastAt = now;
    } catch { /* fall through */ }
    return _origReplace(state, title, url as any);
  } as typeof window.history.replaceState;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const segments = useSegments();
  const setCurrentRoute = useNavigationStore((s) => s.setCurrentRoute);
  const setPendingDeepLink = useNavigationStore((s) => s.setPendingDeepLink);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const user = useAuthStore((s) => s.user);
  const loadSession = useAuthStore((s) => s.loadSession);
  const refreshProfile = useAuthStore((s) => s.refreshProfile);
  const [showNotifRationale, setShowNotifRationale] = useState(false);
  const [biometricLocked, setBiometricLocked] = useState(false);
  const permissionResolveRef = useRef<((proceed: boolean) => void) | null>(null);

  // Capture incoming deep links (both authenticated and unauthenticated)
  useEffect(() => {
    const TAB_ROUTES: Record<string, string> = { chat: '/(tabs)/chat', settings: '/(tabs)/settings', calendar: '/(tabs)/calendar', reminders: '/(tabs)/reminders', reminder: '/(tabs)/calendar' };
    function extractInfo(url: string): { path: string; date?: string } | null {
      try {
        const parsed = Linking.parse(url);
        if (!parsed.path) return null;
        const clean = parsed.path.replace(/^\/+/, '');
        const first = clean.split('/')[0];
        const route = TAB_ROUTES[first] || ('/' + clean);
        const date = parsed.queryParams?.date as string | undefined;
        return { path: route, date };
      } catch { /* ignore */ }
      return null;
    }
    function handleDeepLink(url: string) {
      const info = extractInfo(url);
      if (!info) return;
      if (isAuthenticated) {
        // Already authenticated — navigate immediately
        if (info.date) setPendingDeepLink(info.path, info.date);
        router.replace(info.path as any);
      } else {
        setPendingDeepLink(info.path, info.date);
      }
    }
    // Check initial URL (cold start)
    Linking.getInitialURL().then(url => {
      if (url) handleDeepLink(url);
    });
    // Listen for incoming URLs while app is open
    const sub = Linking.addEventListener('url', ({ url }) => {
      handleDeepLink(url);
    });
    return () => sub.remove();
  }, [isAuthenticated, setPendingDeepLink, router]);

  useEffect(() => {
    let cancelled = false;

    initAnalytics();
    initSentry();

    // Networking must not start until the persisted server selection has been
    // restored. Starting auth first can leave Axios bound to one host while the
    // WebSocket subsequently uses another, which looks like a permanent
    // Offline/Reconnecting loop until the process is restarted.
    const serverReady = (async () => {
      try {
        const url = await getServerUrl();
        const normalized = normalizeServerUrl(url);
        if (!cancelled) {
          setServerUrlOverride(normalized);
          resetClient();
        }
      } catch (error) {
        console.warn('[Init] Failed to restore server URL override:', error);
      }
    })();

    void serverReady.then(() => {
      if (!cancelled) void loadSession();
    });

    (async () => {
      try {
        await loadPersistedLanguage();
      } catch (error) {
        console.warn('[Init] Failed to restore persisted language:', error);
      }

      await serverReady;
      if (cancelled) return;

      try {
        const langs = await fetchLanguages();
        if (!cancelled && langs.length > 0) {
          setLanguages(langs.map(l => ({
            code: l.code,
            label: l.nativeName || l.name,
            flag: l.flag,
            rtl: l.rtl,
            tier: l.tier,
          })));
        }
      } catch {
        // Fallback to built-in list — already set
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Track current route for screen-aware notifications + announce to screen readers
  useEffect(() => {
    const route = segments.join('/');
    setCurrentRoute(route);
    trackScreen(route || 'home');
    addBreadcrumb('navigation', route || 'home');
    // Announce screen change for accessibility
    const screenName = segments[segments.length - 1] || 'home';
    if (Platform.OS !== 'web') {
      AccessibilityInfo.announceForAccessibility(screenName.replace(/[^a-zA-Z]/g, ' ').trim());
    }
  }, [segments]);

  // Request notification permissions, register device, + register background sync once authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const chatStore = useChatStore.getState();
    const remindersStore = useRemindersStore.getState();
    const stopQueueReplay = startOfflineQueueReplay();

    chatStore.initWebSocket();

    (async () => {
      try {
        // On mobile, show rationale modal before OS permission prompt if not yet determined
        let shouldRequest = true;
        if (Platform.OS !== 'web') {
          const Notifications = require('expo-notifications');
          const { status: existing } = await Notifications.getPermissionsAsync();
          if (existing !== 'granted' && existing !== 'denied') {
            // Show rationale, wait for user choice
            shouldRequest = await new Promise<boolean>((resolve) => {
              permissionResolveRef.current = resolve;
              setShowNotifRationale(true);
            });
          }
        }

        const granted = shouldRequest ? await requestPermissions() : false;
        if (cancelled) return;
        if (Platform.OS !== 'web') await registerBackgroundSync();

        // Register device + push token with server
        const deviceId = await getDeviceId();
        const platformName = Platform.OS === 'web' ? 'web' : Platform.OS === 'ios' ? 'ios' : 'android';

        if (granted && Platform.OS === 'web') {
          // Web: register web push subscription via service worker
          try {
            const { registerWebPush } = await import('../src/utils/webNotifications');
            const { getVapidPublicKey } = await import('../src/api/client');
            const vapidKey = await getVapidPublicKey();
            if (vapidKey && !cancelled) {
              const subscription = await registerWebPush(vapidKey);
              if (subscription && !cancelled) {
                registerDevice({ pushToken: subscription, platform: platformName, deviceId }).catch(() => {});
              }
            }
          } catch (e) {
            console.warn('[Init] Web push registration error:', e);
          }
        } else if (granted) {
          // Mobile: register Expo push token
          const pushToken = await getPushToken();
          if (pushToken && !cancelled) {
            registerDevice({ pushToken, platform: platformName, deviceId }).catch(() => {});
          }
        }
      } catch (e) {
        console.warn('[Init] Permission/registration error:', e);
      }
      if (cancelled) return;
      // Initial sync: fetch from server (or reschedule from local if offline)
      remindersStore.initSync();
      remindersStore.syncFromServer();
      // Start widget data sync (native widgets)
      initWidgetSync();
    })();
    return () => {
      cancelled = true;
      stopQueueReplay();
      remindersStore.cleanupSync();
      chatStore.cleanupWebSocket();
      cleanupWidgetSync();
    };
  }, [isAuthenticated]);

  // Handle notification quick actions (Done / Snooze 15m)
  useEffect(() => {
    if (!isAuthenticated || Platform.OS === 'web') return;
    const sub = addNotificationActionListener(
      (seq, occurrenceRunAt) => useRemindersStore.getState().markDone(seq, {
        occurrenceRunAt,
        requireOccurrenceIdentity: true,
        suppressUndoPrompt: true,
      }),
      (seq, mins, occurrenceRunAt) => useRemindersStore.getState().snooze(seq, mins, {
        occurrenceRunAt,
        requireOccurrenceIdentity: true,
      }),
    );
    return () => sub.remove();
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || Platform.OS === 'web') return;

    // Track user properties for analytics + Sentry context
    if (user) {
      setUserProperties({
        language: user.lang || '',
        timezone: user.tz || '',
        subscriptionTier: user.subStatus || 'unsubscribed',
      });
      setSentryUser(user.waId, user.lang || '', user.tz || '');
    }

    // Check biometric lock on app launch
    (async () => {
      const enabled = await isBiometricEnabled();
      if (enabled) {
        setBiometricLocked(true);
        const ok = await authenticateWithBiometric('Unlock FishMemory');
        setBiometricLocked(!ok);
      }
    })();

    const subscription = AppState.addEventListener('change', (state) => {
      if (state === 'active') {
        refreshProfile();
      }
    });

    return () => {
      subscription.remove();
    };
  }, [isAuthenticated, refreshProfile]);

  const lastNavTargetRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading) return;

    const inAuth = segments[0] === '(auth)';
    const inPublicCheckout = segments[0] === 'checkout';
    const inPublicSubscribe = segments[0] === 'subscribe';
    const currentPath = '/' + segments.join('/');
    const needsSetup = needsInitialSetup(user);
    const isPublicRoute = inPublicCheckout || inPublicSubscribe;

    let target: string | null = null;
    if (!isAuthenticated && !inAuth && !isPublicRoute) {
      target = '/(auth)/phone';
    } else if (isAuthenticated && inAuth && needsSetup && (segments.length < 2 || (segments as string[])[1] !== 'setup')) {
      target = '/(auth)/setup';
    } else if (isAuthenticated && inAuth && !needsSetup) {
      const pending = useNavigationStore.getState().consumePendingDeepLink();
      target = pending || '/(tabs)/chat';
    } else if (isAuthenticated && !inAuth && !isPublicRoute && needsSetup) {
      target = '/(auth)/setup';
    }

    if (!target) return;
    // Don't re-issue the same navigation we just issued, and don't
    // navigate to a path equivalent to where we already are. This prevents
    // the layout effect from looping when `user`/`segments` references
    // change but the routing decision is unchanged.
    if (lastNavTargetRef.current === target) return;
    const targetPath = target.replace(/\(auth\)\//, '').replace(/\(tabs\)\//, '');
    if (currentPath === '/' + targetPath || currentPath === target) {
      lastNavTargetRef.current = target;
      return;
    }
    lastNavTargetRef.current = target;
    router.replace(target as any);
  }, [isAuthenticated, isLoading, segments, user, router]);

  const { colors } = useTheme();

  if (isLoading) {
    return (
      <View style={[styles.loading, { backgroundColor: colors.background }]}>
        <Image source={require('../assets/logo.png')} style={styles.splashLogo} />
        <Text style={[styles.splashTitle, { color: colors.text }]}>FishMemory</Text>
        <ActivityIndicator size="small" color={colors.primary} style={{ marginTop: 16 }} />
      </View>
    );
  }

  if (biometricLocked) {
    return (
      <View style={[styles.loading, { backgroundColor: colors.background }]}>
        <Image source={require('../assets/logo.png')} style={styles.splashLogo} />
        <Text style={[styles.splashTitle, { color: colors.text }]}>FishMemory</Text>
        <Text style={{ color: colors.textSecondary, marginTop: 12, fontSize: 14 }}>Locked</Text>
        <TouchableOpacity
          onPress={async () => {
            const ok = await authenticateWithBiometric('Unlock FishMemory');
            if (ok) setBiometricLocked(false);
          }}
          style={{ marginTop: 20, paddingHorizontal: 24, paddingVertical: 12, backgroundColor: colors.primary, borderRadius: 10 }}
        >
          <Text style={{ color: '#fff', fontWeight: '600' }}>Unlock</Text>
        </TouchableOpacity>
      </View>
    );
  }

  return (
    <>
      {children}
      <NotificationRationale
        visible={showNotifRationale}
        onAllow={() => {
          setShowNotifRationale(false);
          permissionResolveRef.current?.(true);
          permissionResolveRef.current = null;
        }}
        onLater={() => {
          setShowNotifRationale(false);
          permissionResolveRef.current?.(false);
          permissionResolveRef.current = null;
        }}
      />
    </>
  );
}

function AppContent() {
  const { colors } = useTheme();
  return (
    <>
      <StatusBar style={colors.statusBar as 'light' | 'dark'} />
      <ErrorBoundary>
      <InAppToast />
      <AuthGate>
            <Stack screenOptions={{ headerShown: false }}>
              <Stack.Screen name="(auth)" />
              <Stack.Screen name="(tabs)" />
              <Stack.Screen name="checkout" />
              <Stack.Screen name="subscribe" />
              <Stack.Screen
                name="reminder/[seq]"
                options={{
                  presentation: 'modal',
                  animation: 'slide_from_bottom',
                }}
              />
            </Stack>
      </AuthGate>
      </ErrorBoundary>
    </>
  );
}

SplashScreen.preventAutoHideAsync();

// Support Dynamic Type / font scaling with a reasonable upper bound
if (!(Text as any).defaultProps) (Text as any).defaultProps = {};
(Text as any).defaultProps.maxFontSizeMultiplier = 1.5;

export default function RootLayout() {
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    PlusJakartaSans_600SemiBold,
    PlusJakartaSans_700Bold,
  });

  useEffect(() => {
    if (fontsLoaded) SplashScreen.hideAsync();
  }, [fontsLoaded]);

  if (!fontsLoaded) return null;

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <I18nextProvider i18n={i18n}>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </I18nextProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  splashLogo: {
    width: 72,
    height: 72,
    resizeMode: 'contain',
  },
  splashTitle: {
    fontSize: 20,
    fontFamily: 'PlusJakartaSans_600SemiBold',
    marginTop: 12,
    letterSpacing: 0.5,
  },
});
